import React, { useState, useCallback } from 'react'
import { UserService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'

interface ActionsSectionProps {
  userId: string
  userName: string
  onUserDeleted?: () => void
}

interface ResourceSectionProps {
  label: string
  data: unknown[]
  onCopy: () => void
  copyLabel: string
}

const ResourceSection: React.FC<ResourceSectionProps> = ({ label, data, onCopy, copyLabel }) => {
  if (data.length === 0) return null
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {label} ({data.length})
        </label>
        <button
          onClick={onCopy}
          className="text-xs px-2 py-1 text-muted-foreground border border-border rounded hover:bg-secondary/10 transition-colors"
        >
          {copyLabel}
        </button>
      </div>
      <textarea
        readOnly
        value={JSON.stringify(data, null, 2)}
        className="w-full h-32 text-xs font-mono bg-secondary/5 border border-border rounded-md p-2 resize-y text-foreground"
      />
    </div>
  )
}

export const ActionsSection: React.FC<ActionsSectionProps> = ({
  userId,
  userName,
  onUserDeleted,
}) => {
  const { t } = useTar()
  const [isRunning, setIsRunning] = useState(false)
  const [dryRunResult, setDryRunResult] = useState<UserService.AdminDeleteUserResponse | null>(null)
  const [deleteResult, setDeleteResult] = useState<UserService.AdminDeleteUserResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runDryDelete = useCallback(async () => {
    setError(null)
    setIsRunning(true)
    setDeleteResult(null)
    try {
      const response = await apis.userService.adminActionsDeleteUser(userId, { dryRun: true })
      setDryRunResult(response.data)
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || t('adminActions.dryRunFailed')
      setError(errorMessage)
    } finally {
      setIsRunning(false)
    }
  }, [userId, t])

  const runActualDelete = useCallback(async () => {
    const confirmed = window.confirm(t('adminActions.confirmDeleteUser', { userName }))
    if (!confirmed) return
    setError(null)
    setIsRunning(true)
    try {
      const response = await apis.userService.adminActionsDeleteUser(userId, { dryRun: false })
      setDeleteResult(response.data)
      setDryRunResult(null)
      onUserDeleted?.()
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || t('adminActions.deleteUserFailed')
      setError(errorMessage)
    } finally {
      setIsRunning(false)
    }
  }, [userId, userName, onUserDeleted, t])

  const copyToClipboard = useCallback((data: unknown) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2))
  }, [])

  const copyAllToClipboard = useCallback(() => {
    if (!dryRunResult) return
    const allData = {
      user: dryRunResult.user,
      challenges: dryRunResult.challenges,
      assignments: dryRunResult.assignments,
      cohorts: dryRunResult.cohorts,
      persons: dryRunResult.persons,
      invitations: dryRunResult.invitations,
      sessions: dryRunResult.sessions,
      activityEvents: dryRunResult.activityEvents,
    }
    navigator.clipboard.writeText(JSON.stringify(allData, null, 2))
  }, [dryRunResult])

  const result = dryRunResult || deleteResult

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('adminActions.deleteUser')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('adminActions.deleteUserDescription', { userName })}
        </p>

        <div className="flex gap-3">
          <button
            onClick={runDryDelete}
            disabled={isRunning}
            className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-secondary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning && !dryRunResult ? t('adminActions.running') : t('adminActions.dryRun')}
          </button>

          <button
            onClick={runActualDelete}
            disabled={isRunning || !dryRunResult || !!deleteResult}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning && dryRunResult ? t('adminActions.deleting') : t('adminActions.deleteUserButton')}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {deleteResult?.deletedCounts && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md space-y-2">
          <p className="text-sm font-medium text-green-600">{t('adminActions.userDeletedSuccessfully')}</p>
          <div className="text-xs text-green-600 grid grid-cols-2 gap-1">
            <span>{t('adminActions.challenges')}: {deleteResult.deletedCounts.challenges}</span>
            <span>{t('adminActions.persons')}: {deleteResult.deletedCounts.persons}</span>
            <span>{t('adminActions.invitations')}: {deleteResult.deletedCounts.invitations}</span>
            <span>{t('adminActions.sessions')}: {deleteResult.deletedCounts.sessions}</span>
            <span>{t('adminActions.activityEvents')}: {deleteResult.deletedCounts.activityEvents}</span>
          </div>
          <div className="text-xs text-green-600 grid grid-cols-2 gap-1">
            <span>{t('adminActions.assignments')}: {deleteResult.deletedCounts.assignments}</span>
            <span>{t('adminActions.cohorts')}: {deleteResult.deletedCounts.cohorts}</span>
          </div>
        </div>
      )}

      {result && !deleteResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-muted-foreground">
              {t('adminActions.resourcesFound', { email: result.user.email })}
            </h4>
            <button
              onClick={copyAllToClipboard}
              className="text-xs px-3 py-1.5 text-muted-foreground border border-border rounded-md hover:bg-secondary/10 transition-colors"
            >
              {t('adminActions.copyAll')}
            </button>
          </div>

          <h5 className="text-xs font-semibold text-red-500 uppercase tracking-wide">{t('adminActions.willBeDeleted')}</h5>

          <ResourceSection
            label={t('adminActions.challenges')}
            data={result.challenges}
            onCopy={() => copyToClipboard(result.challenges)}
            copyLabel={t('adminActions.copy')}
          />
          <ResourceSection
            label={t('adminActions.persons')}
            data={result.persons}
            onCopy={() => copyToClipboard(result.persons)}
            copyLabel={t('adminActions.copy')}
          />
          <ResourceSection
            label={t('adminActions.invitations')}
            data={result.invitations}
            onCopy={() => copyToClipboard(result.invitations)}
            copyLabel={t('adminActions.copy')}
          />
          <ResourceSection
            label={t('adminActions.activityEvents')}
            data={result.activityEvents}
            onCopy={() => copyToClipboard(result.activityEvents)}
            copyLabel={t('adminActions.copy')}
          />

          <div className="text-xs text-muted-foreground p-2 bg-secondary/5 border border-border rounded-md">
            {t('adminActions.sessions')}: {result.sessions.count}
          </div>

          <h5 className="text-xs font-semibold text-yellow-500 uppercase tracking-wide pt-2">{t('adminActions.willBeRemovedFrom')}</h5>

          <ResourceSection
            label={t('adminActions.assignments')}
            data={result.assignments}
            onCopy={() => copyToClipboard(result.assignments)}
            copyLabel={t('adminActions.copy')}
          />
          <ResourceSection
            label={t('adminActions.cohorts')}
            data={result.cohorts}
            onCopy={() => copyToClipboard(result.cohorts)}
            copyLabel={t('adminActions.copy')}
          />
        </div>
      )}
    </div>
  )
}
